import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  Briefcase, ClipboardList, Map as MapIcon, CheckCircle2, ArrowUpRight, Inbox, TrendingUp, Sparkles, Play, Plus,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { applicableTypes, TYPE_LABEL, TYPE_COLORS } from "@/lib/permissions";
import MyGrowth from "@/components/MyGrowth";
import GuidedJourneyWidget from "@/components/GuidedJourneyWidget";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="border border-zinc-200 p-5 flex flex-col gap-3 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="overline text-zinc-500">{label}</span>
        <div className={`w-8 h-8 grid place-items-center ${accent || "bg-zinc-100 text-zinc-700"}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display font-black text-3xl tracking-tight">{value}</div>
    </div>
  );
}

const CHART_TOOLTIP_STYLE = { border: "1px solid #e4e4e7", borderRadius: 0 };

const ROLE_LABEL = {
  entrepreneur: "Entrepreneur",
  business_owner: "Business Owner",
  investor: "Investor",
  contractor: "Contractor",
  influencer: "Influencer",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [opps, setOpps] = useState([]);

  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/opportunities").then(({ data }) => setOpps(data.slice(0, 5))).catch(() => {});
  }, []);

  // Filter opportunities to those this user can apply to (based on role)
  const myApplicableTypes = useMemo(
    () => applicableTypes(user?.role, user?.is_admin).map((t) => t.value),
    [user?.role, user?.is_admin]
  );

  useEffect(() => {
    if (!opps?.length || !myApplicableTypes.length) {
      setRecommended([]);
      return;
    }
    setRecommended(
      opps
        .filter((o) => myApplicableTypes.includes(o.type) && o.owner_id !== user?.user_id && o.status === "open")
        .slice(0, 6)
    );
  }, [opps, myApplicableTypes, user?.user_id]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="tag" data-testid="dashboard-tag">
            <span className="w-1.5 h-1.5 bg-[#C8102E]" /> {ROLE_LABEL[user?.role] || "Member"}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">
            Welcome back, {user?.name?.split(" ")[0] || "operator"}.
          </h1>
          <p className="text-zinc-600 mt-1 text-sm">Your network at a glance.</p>
        </div>
        <Link
          to="/opportunities"
          data-testid="dashboard-explore-btn"
          className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2.5 font-semibold transition-colors text-sm"
        >
          Explore opportunities <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-open-opps" icon={Briefcase} label="Open opportunities" value={stats?.open_opportunities ?? "—"} accent="bg-[#C8102E] text-white" />
        <StatCard testid="stat-my-apps" icon={ClipboardList} label="My applications" value={stats?.my_applications ?? "—"} />
        <StatCard testid="stat-received" icon={Inbox} label="Received on my posts" value={stats?.received_applications ?? "—"} accent="bg-[#94959A] text-white" />
        <StatCard testid="stat-accepted" icon={CheckCircle2} label="Accepted for me" value={stats?.accepted_applications ?? "—"} />
      </div>

      {/* Guided Growth — Your Next Step (Sprint 2E mentor layer) */}
      <GuidedJourneyWidget />

      {/* Growth Engine — referrals + commissions + badges + recommendations */}
      <MyGrowth />

      {/* OPPORTUNITY TV — featured magnet card */}
      <div
        className="relative overflow-hidden border border-zinc-800"
        style={{ background: "linear-gradient(135deg, #09090b 0%, #1f1f23 55%, #C8102E 160%)" }}
        data-testid="dashboard-tv-card"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25 blur-3xl" style={{ background: "#E0AA3E" }} />
        <div className="relative p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <div className="flex-1 text-white">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 bg-white/10 backdrop-blur border border-white/15">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#E0AA3E" }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#E0AA3E" }} />
              </span>
              Live · Opportunity TV
            </div>
            <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tight mt-3">
              Real Malaysians making it happen — on video.
            </h3>
            <p className="text-white/80 text-sm mt-2 max-w-2xl">
              Watch 60-second stories from real members, then post your own to recruit your team. Your video, your network, your income.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full lg:w-auto">
            <Link to="/tv" className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 px-5 py-3 text-sm font-bold hover:bg-zinc-100" data-testid="dashboard-tv-watch-btn">
              <Play className="w-4 h-4" /> Watch Now
            </Link>
            <Link to="/post-promo" className="inline-flex items-center justify-center gap-2 text-white border border-white/30 px-5 py-3 text-sm font-semibold hover:bg-white/10" data-testid="dashboard-tv-post-btn">
              <Plus className="w-4 h-4" /> Post Your Story
            </Link>
          </div>
        </div>
      </div>

      {/* Recommended for you — role-personalized */}
      <div className="border border-zinc-200 bg-white" data-testid="recommended-section">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline text-zinc-500 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#94959A]" /> Recommended for you
            </div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1">
              {recommended.length > 0
                ? `Best matches for ${ROLE_LABEL[user?.role] || "you"}`
                : "Personalized matches will appear here"}
            </h3>
          </div>
          <Link to="/opportunities" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] hover:underline" data-testid="recommended-see-all">
            See all →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="recommended-grid">
          {recommended.length === 0 && (
            <div className="p-8 text-center text-zinc-500 text-sm col-span-full">
              {myApplicableTypes.length === 0
                ? "Your current role doesn't have application rights in v1. Update your role from Profile to see recommendations."
                : "No matching opportunities yet — check back soon."}
            </div>
          )}
          {recommended.map((o) => (
            <Link
              key={o.opportunity_id}
              to="/opportunities"
              className="border-b border-r border-zinc-200 p-5 hover:bg-zinc-50 transition-colors"
              data-testid={`recommended-opp-${o.opportunity_id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${TYPE_COLORS[o.type]}`}>
                  {TYPE_LABEL[o.type]}
                </span>
              </div>
              <h4 className="font-display font-black text-base tracking-tight mt-2 line-clamp-1">{o.title}</h4>
              <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{o.description || "—"}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                <span className="font-mono truncate">by {o.owner_name || "—"}</span>
                {Number(o.budget) > 0 && <span className="font-mono font-semibold">${Number(o.budget).toLocaleString()}</span>}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="overline text-zinc-500">Marketplace</div>
              <h3 className="font-display font-black text-xl tracking-tight">Recent opportunities</h3>
            </div>
            <Link to="/opportunities" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] hover:underline" data-testid="dashboard-see-all-opps">
              See all →
            </Link>
          </div>
          <div className="divide-y divide-zinc-100" data-testid="recent-opportunities">
            {opps.length === 0 && (
              <div className="p-6 text-center text-zinc-500 text-sm">No opportunities yet. Be the first — post one.</div>
            )}
            {opps.map((o) => (
              <Link
                key={o.opportunity_id}
                to="/opportunities"
                className="flex items-center justify-between p-4 hover:bg-zinc-50"
                data-testid={`recent-opp-${o.opportunity_id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.title}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mt-0.5">{o.type} · {o.location_name || "Remote"}</div>
                </div>
                {Number(o.budget) > 0 && (
                  <div className="font-mono text-sm font-semibold whitespace-nowrap pl-3">
                    ${Number(o.budget).toLocaleString()}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>

        <div className="border border-zinc-200 bg-white">
          <div className="p-5 border-b border-zinc-200">
            <div className="overline text-zinc-500">Distribution</div>
            <h3 className="font-display font-black text-xl tracking-tight">Opportunity types</h3>
          </div>
          <div className="h-72 p-3" data-testid="chart-types">
            <ResponsiveContainer>
              <BarChart data={stats?.type_series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="type" stroke="#52525b" fontSize={12} />
                <YAxis stroke="#52525b" fontSize={12} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#C8102E" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 border border-zinc-200">
        <div className="p-6 sm:border-r border-zinc-200" data-testid="quick-link-opps">
          <div className="flex items-center gap-2 overline text-zinc-500">
            <Briefcase className="w-4 h-4 text-[#C8102E]" /> Posted by me
          </div>
          <div className="font-display font-black text-3xl mt-2">{stats?.my_opportunities ?? 0}</div>
          <Link to="/opportunities" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] mt-3 inline-block hover:underline">
            Manage →
          </Link>
        </div>
        <div className="p-6 sm:border-r border-zinc-200" data-testid="quick-link-territories">
          <div className="flex items-center gap-2 overline text-zinc-500">
            <MapIcon className="w-4 h-4 text-[#94959A]" /> My territories
          </div>
          <div className="font-display font-black text-3xl mt-2">{stats?.my_territories ?? 0}</div>
          <Link to="/territories" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] mt-3 inline-block hover:underline">
            Open board →
          </Link>
        </div>
        <div className="p-6" data-testid="quick-link-apps">
          <div className="flex items-center gap-2 overline text-zinc-500">
            <TrendingUp className="w-4 h-4 text-emerald-600" /> Acceptance rate
          </div>
          <div className="font-display font-black text-3xl mt-2">
            {stats && stats.my_applications > 0
              ? `${Math.round((stats.accepted_applications / stats.my_applications) * 100)}%`
              : "—"}
          </div>
          <Link to="/applications" className="text-xs uppercase tracking-widest font-semibold text-[#C8102E] mt-3 inline-block hover:underline">
            View applications →
          </Link>
        </div>
      </div>
    </div>
  );
}
